<template>
  <div class="p-4 space-y-4">
    <!-- Name (disabled) -->
    <div>
      <label class="block text-sm font-medium text-gray-700">Name</label>
      <InputText v-model="formName" class="w-full" disabled />
    </div>

    <!-- Add New Value -->
    <div>
      <label class="block text-sm font-medium text-gray-700">New Value</label>
      <div class="flex gap-2">
        <InputText
          v-model="newValue"
          placeholder="Enter value"
          class="flex-1"
          @keyup.enter="onAddValue"
        />
        <Button label="Add" icon="pi pi-plus" :disabled="!newValue" @click="onAddValue" />
      </div>
    </div>

    <!-- Existing Values -->
    <div v-if="formValues.length">
      <label class="block text-sm font-medium text-gray-700">Values</label>
      <ul class="list-disc list-inside space-y-1">
        <li v-for="val in formValues" :key="val.value" class="flex justify-between items-center">
          <span>
            {{ val.value }}
            <span v-if="val.isActive === false" class="text-gray-500 text-sm ml-1">(inactive)</span>
          </span>
          <div class="flex gap-1">
            <Button
              icon="pi pi-trash"
              class="p-button-text p-button-danger"
              :disabled="loading"
              @click="onDeleteValue(val.value)"
            />
            <Button
              v-if="!val.isActive"
              icon="pi pi-play-circle"
              class="p-button-text p-button-success"
              :disabled="loading"
              @click="onActivateValue(val.value)"
            />
            <Button
              v-else
              icon="pi pi-pause-circle"
              class="p-button-text p-button-warning"
              :disabled="loading"
              @click="onDeactivateValue(val.value)"
            />
          </div>
        </li>
      </ul>
    </div>

    <!-- Error (PrimeVue 4 Message has no `text` prop — use default slot) -->
    <Message v-if="error" severity="error">{{ error }}</Message>

    <!-- Actions -->
    <div class="flex justify-end space-x-2">
      <Button label="Cancel" class="p-button-secondary" @click="onHide" />
      <ProgressSpinner v-if="loading" />
    </div>
  </div>
</template>

<script>
import InputText from 'primevue/inputtext';
import Button from 'primevue/button';
import Message from 'primevue/message';
import ProgressSpinner from 'primevue/progressspinner';
import { mapState, mapActions } from 'vuex';
import enumsService, { USER_ROLES_ENUM_NAME } from '../../../services/enumService';

export default {
  name: 'EditEnumDialog',
  components: {
    InputText,
    Button,
    Message,
    ProgressSpinner,
  },
  props: {
    visible: Boolean,
    enumData: {
      type: Object,
      required: true,
    },
  },
  data() {
    return {
      formName: '',
      formValues: [], // array of { value, isActive, description, createdAt }
      newValue: '',
    };
  },
  computed: {
    ...mapState('enums', {
      loading: (state) => state.loading,
      error: (state) => state.error,
    }),
  },
  watch: {
    visible: {
      immediate: true,
      handler(val) {
        if (val) {
          this.clearError();
          this.formName = this.enumData?.name || '';
          const vals = this.enumData?.values;
          this.formValues = Array.isArray(vals) ? vals.map((v) => ({ ...v })) : [];
          this.newValue = '';
        }
      },
    },
  },
  methods: {
    ...mapActions('enums', [
      'addEnumValue',
      'deactivateEnumValue',
      'activateEnumValue',
      'deleteEnumValue',
      'clearError',
    ]),
    onHide() {
      this.clearError();
      this.$emit('update:visible', false);
    },
    async onAddValue() {
      if (!this.newValue.trim()) return;
      try {
        const response = await this.addEnumValue({
          enumName: this.enumData.name,
          valueData: { value: this.newValue.trim() },
        });
        if (response?.values) {
          this.formValues = response.values;
        }
        this.newValue = '';
      } catch (e) {
        // error in store.error
      }
    },
    async onDeleteValue(value) {
      try {
        let shouldDelete = false;
        if (this.enumData.name === USER_ROLES_ENUM_NAME) {
          const { data } = await enumsService.getUserRoleDeleteImpact(
            USER_ROLES_ENUM_NAME,
            value,
          );
          const msg =
            `Deleting role "${value}" will remove it from ${data.userCount} user(s) ` +
            `and ${data.schemaCount} assessment schema(s).`;
          shouldDelete = await new Promise((resolve) => {
            this.$confirm.require({
              message: msg,
              header: 'Delete role',
              icon: 'pi pi-exclamation-triangle',
              acceptLabel: 'Delete',
              rejectLabel: 'Cancel',
              acceptClass: 'p-button-danger',
              accept: () => resolve(true),
              reject: () => resolve(false),
            });
          });
        } else {
          shouldDelete = await new Promise((resolve) => {
            this.$confirm.require({
              message: `Delete value "${value}" from this enum?`,
              header: 'Delete enum value',
              icon: 'pi pi-exclamation-triangle',
              acceptLabel: 'Delete',
              rejectLabel: 'Cancel',
              acceptClass: 'p-button-danger',
              accept: () => resolve(true),
              reject: () => resolve(false),
            });
          });
        }

        if (!shouldDelete) {
          return;
        }        
        const resp = await this.deleteEnumValue({
          enumName: this.enumData.name,
          value,
        });
        if (resp?.values) {
          this.formValues = resp.values;
        }
      } catch (error) {
        // store.error + toast from Vuex
      }
    },
    async onDeactivateValue(value) {
      try {
        const resp = await this.deactivateEnumValue({
          enumName: this.enumData.name,
          value,
        });
        if (resp?.values) {
          this.formValues = resp.values;
        }
      } catch (error) {
        // store.error + toast
      }
    },
    async onActivateValue(value) {
      try {
        const resp = await this.activateEnumValue({
          enumName: this.enumData.name,
          value,
        });
        if (resp?.values) {
          this.formValues = resp.values;
        }
      } catch (error) {
        // store.error + toast
      }
    },
  },
};
</script>

<style scoped></style>
